import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAdmin } from '@/contexts/AdminContext';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  Facebook, RefreshCw, Settings, Unplug, Zap, BarChart3, Users,
  TrendingUp, ArrowUpRight, Clock, CheckCircle2, XCircle, AlertTriangle
} from 'lucide-react';
import logger from '@/utils/logger';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, Legend
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Stable tick style objects to avoid re-renders in Recharts
const TICK_SM = { fontSize: 10 };
const TICK_MD = { fontSize: 11 };

const STAGE_COLORS = {
  new: '#3b82f6', contacted: '#f59e0b', proposal_sent: '#8b5cf6',
  negotiation: '#ec4899', follow_up: '#06b6d4', won: '#10b981',
  lost: '#ef4444', ghosted: '#6b7280',
};

const CHART_COLORS = ['#3b82f6', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#6b7280'];

export default function MetaAdsDashboard() {
  const { getAuthHeaders } = useAdmin();
  const [connection, setConnection] = useState(null);
  const [stats, setStats] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [dateRange, setDateRange] = useState('this_month');

  const headers = getAuthHeaders();

  const fetchAll = useCallback(async () => {
    try {
      const hdrs = getAuthHeaders();
      const [connRes, statsRes, campRes] = await Promise.all([
        fetch(`${API_URL}/api/meta-ads/connection`, { headers: hdrs }),
        fetch(`${API_URL}/api/meta-ads/stats`, { headers: hdrs }),
        fetch(`${API_URL}/api/meta-ads/campaigns`, { headers: hdrs }),
      ]);
      setConnection(await connRes.json());
      setStats(await statsRes.json());
      const campData = await campRes.json();
      setCampaigns(campData.campaigns || []);
    } catch (e) {
      logger.error(e);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const res = await fetch(`${API_URL}/api/meta-ads/connect`, { method: 'POST', headers });
      const data = await res.json();
      if (data.auth_url) {
        window.open(data.auth_url, '_blank');
        toast.info('Complete the Facebook authorization in the new tab.');
      } else {
        toast.error(data.detail || 'Failed to initiate connection');
      }
    } catch (e) {
      toast.error('Meta App credentials not configured yet.');
    } finally {
      setConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm('Disconnect your Meta Ads account? Existing leads will be preserved.')) return;
    try {
      await fetch(`${API_URL}/api/meta-ads/disconnect`, { method: 'DELETE', headers });
      toast.success('Disconnected from Meta Ads');
      fetchAll();
    } catch (e) {
      toast.error('Failed to disconnect');
    }
  };

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await fetch(`${API_URL}/api/meta-ads/leads/sync`, { method: 'POST', headers });
      const data = await res.json();
      if (data.success) {
        toast.success(`Sync complete: ${data.leads_new} new leads, ${data.leads_duplicate} duplicates skipped`);
        fetchAll();
      } else {
        toast.error(data.error || 'Sync failed');
      }
    } catch (e) {
      toast.error('Sync failed');
    } finally {
      setSyncing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="animate-spin text-slate-400" size={32} />
      </div>
    );
  }

  // Not connected — show connect page
  if (!connection?.connected) {
    return (
      <div className="max-w-2xl mx-auto mt-12">
        <div className="bg-white rounded-2xl shadow-lg border p-8 text-center" data-testid="meta-connect-card">
          <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Facebook className="text-blue-600" size={32} />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Connect Meta Ads</h2>
          <p className="text-slate-500 mb-8 max-w-md mx-auto">
            Sync leads from your Facebook & Instagram ad campaigns directly into your CRM — automatically.
          </p>
          <div className="text-left max-w-sm mx-auto space-y-3 mb-8">
            {[
              'Pull leads from all Lead Generation campaigns',
              'Campaign & ad-set level filtering',
              'Real-time webhook sync (instant lead delivery)',
              'Smart duplicate detection',
              'Full lead management with stage tracking',
            ].map((item, i) => (
              <div key={`feature-${item.slice(0, 20)}`} className="flex items-start gap-2 text-sm text-slate-600">
                <CheckCircle2 size={16} className="text-green-500 mt-0.5 shrink-0" />
                <span>{item}</span>
              </div>
            ))}
          </div>
          <Button
            onClick={handleConnect}
            disabled={connecting}
            className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-xl text-base"
            data-testid="connect-meta-btn"
          >
            {connecting ? <RefreshCw className="animate-spin mr-2" size={18} /> : <Facebook className="mr-2" size={18} />}
            Connect with Facebook
          </Button>
          <p className="text-xs text-slate-400 mt-4">
            Requires: Facebook Business Manager access<br />
            Permissions: ads_read, leads_retrieval, pages_read_engagement
          </p>
        </div>
      </div>
    );
  }

  // Connected — show dashboard
  const tokenExpiry = connection.token_expires_at ? new Date(connection.token_expires_at) : null;
  const daysUntilExpiry = tokenExpiry ? Math.ceil((tokenExpiry - new Date()) / (1000 * 60 * 60 * 24)) : null;

  const stageDonutData = stats?.stage_distribution
    ? Object.entries(stats.stage_distribution).filter(([, v]) => v > 0).map(([k, v]) => ({ name: k.replace('_', ' '), value: v }))
    : [];

  return (
    <div className="space-y-6" data-testid="meta-ads-dashboard">
      {/* Token warning */}
      {daysUntilExpiry !== null && daysUntilExpiry <= 7 && daysUntilExpiry > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3">
          <AlertTriangle className="text-amber-500 shrink-0" size={20} />
          <p className="text-sm text-amber-700 flex-1">
            Your Meta connection expires in <strong>{daysUntilExpiry} days</strong>. Reconnect to avoid interruption.
          </p>
          <Button size="sm" variant="outline" onClick={handleConnect} className="border-amber-400 text-amber-700 hover:bg-amber-100">
            Reconnect
          </Button>
        </div>
      )}

      {/* Connection Status Card */}
      <div className="bg-white rounded-xl shadow-sm border p-5" data-testid="connection-status-card">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" />
            <div>
              <h3 className="font-semibold text-slate-900">Connected to Meta Ads</h3>
              <p className="text-sm text-slate-500">
                {connection.meta_user_name || 'Meta Account'}
                {connection.connected_at && ` · Connected ${new Date(connection.connected_at).toLocaleDateString()}`}
                {connection.connected_by_name && ` by ${connection.connected_by_name}`}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={handleSync} disabled={syncing} data-testid="sync-now-btn"
              className="bg-blue-600 hover:bg-blue-700 text-white">
              <RefreshCw className={`mr-1.5 ${syncing ? 'animate-spin' : ''}`} size={14} />
              {syncing ? 'Syncing...' : 'Sync Now'}
            </Button>
            <Button size="sm" variant="outline" onClick={handleDisconnect} data-testid="disconnect-btn"
              className="text-red-500 border-red-200 hover:bg-red-50">
              <Unplug className="mr-1.5" size={14} /> Disconnect
            </Button>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4 pt-4 border-t">
          <div className="text-center">
            <p className="text-2xl font-bold text-slate-900">{connection.ad_accounts_count}</p>
            <p className="text-xs text-slate-500">Ad Accounts</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-slate-900">{connection.campaigns_count}</p>
            <p className="text-xs text-slate-500">Campaigns ({connection.active_campaigns_count} active)</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-slate-900">{connection.total_leads?.toLocaleString()}</p>
            <p className="text-xs text-slate-500">Total Leads</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-slate-500">Last Sync</p>
            <p className="text-sm font-medium text-slate-700">
              {connection.last_sync_at ? new Date(connection.last_sync_at).toLocaleString() : 'Never'}
            </p>
          </div>
        </div>
      </div>

      {/* Stats Strip */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="stats-strip">
          {[
            { label: 'Total Leads', value: stats.total_leads, icon: Users, color: 'blue' },
            { label: 'New Today', value: stats.new_today, icon: Zap, color: 'green' },
            { label: 'Contacted', value: stats.contacted, icon: TrendingUp, color: 'amber' },
            { label: 'Won', value: stats.won, icon: CheckCircle2, color: 'emerald' },
            { label: 'Lost', value: stats.lost, icon: XCircle, color: 'red' },
            { label: 'Unassigned', value: stats.unassigned, icon: AlertTriangle, color: 'orange' },
            { label: 'This Week', value: stats.this_week, icon: BarChart3, color: 'purple' },
            { label: 'This Month', value: stats.this_month, icon: ArrowUpRight, color: 'indigo' },
          ].map(({ label, value, icon: Icon, color }) => (
            <div key={label} className="bg-white rounded-xl border p-4 hover:shadow-sm transition-shadow" data-testid={`stat-${label.toLowerCase().replace(' ', '-')}`}>
              <div className="flex items-center justify-between mb-2">
                <Icon size={18} className={`text-${color}-500`} />
                <span className="text-xs text-slate-400">{label}</span>
              </div>
              <p className="text-2xl font-bold text-slate-900">{value?.toLocaleString() || 0}</p>
            </div>
          ))}
        </div>
      )}

      {/* Campaign Cards */}
      {campaigns.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-slate-900 mb-3">Campaign Performance</h3>
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
            {campaigns.map(camp => (
              <div key={camp.id} className="bg-white rounded-xl border p-5 hover:shadow-sm transition-shadow" data-testid={`campaign-card-${camp.id}`}>
                <div className="flex items-center gap-2 mb-2">
                  <span className={`w-2 h-2 rounded-full ${
                    camp.campaign_status === 'ACTIVE' ? 'bg-green-500' :
                    camp.campaign_status === 'PAUSED' ? 'bg-yellow-500' : 'bg-slate-400'
                  }`} />
                  <span className="text-xs font-medium uppercase text-slate-400">{camp.campaign_status}</span>
                </div>
                <h4 className="font-semibold text-slate-900 mb-3 truncate">{camp.campaign_name}</h4>
                <div className="grid grid-cols-2 gap-y-2 text-sm">
                  <div><span className="text-slate-400">Leads:</span> <strong>{camp.lead_count}</strong></div>
                  <div><span className="text-slate-400">New Today:</span> <strong>{camp.new_today}</strong></div>
                  <div><span className="text-slate-400">Contacted:</span> <strong>{camp.contacted}</strong></div>
                  <div><span className="text-slate-400">Won:</span> <strong>{camp.won}</strong></div>
                  <div className="col-span-2"><span className="text-slate-400">Conv. Rate:</span> <strong>{camp.conversion_rate}%</strong></div>
                </div>
                <div className="mt-3 pt-3 border-t flex items-center justify-between text-xs text-slate-400">
                  <span>{camp.forms_count} form{camp.forms_count !== 1 ? 's' : ''}</span>
                  <span>{camp.last_lead_at ? `Last: ${new Date(camp.last_lead_at).toLocaleDateString()}` : 'No leads yet'}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Charts */}
      {stats && (
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Daily Lead Volume */}
          {stats.daily_leads?.length > 0 && (
            <div className="bg-white rounded-xl border p-5">
              <h4 className="font-semibold text-slate-900 mb-4">Lead Volume (Last 30 Days)</h4>
              <ResponsiveContainer width="100%" height={250}>
                <LineChart data={stats.daily_leads}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="date" tick={TICK_SM} tickFormatter={v => v.slice(5)} />
                  <YAxis tick={TICK_MD} allowDecimals={false} />
                  <Tooltip />
                  <Line type="monotone" dataKey="count" stroke="#3b82f6" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Stage Distribution Donut */}
          {stageDonutData.length > 0 && (
            <div className="bg-white rounded-xl border p-5">
              <h4 className="font-semibold text-slate-900 mb-4">Lead Stage Distribution</h4>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie data={stageDonutData} cx="50%" cy="50%" innerRadius={55} outerRadius={90}
                    paddingAngle={3} dataKey="value" nameKey="name" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                    {stageDonutData.map((entry, i) => (
                      <Cell key={`stage-${entry.name}`} fill={STAGE_COLORS[entry.name.replace(' ', '_')] || CHART_COLORS[i % CHART_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Leads by Campaign */}
          {stats.campaign_leads?.length > 0 && (
            <div className="bg-white rounded-xl border p-5">
              <h4 className="font-semibold text-slate-900 mb-4">Leads by Campaign</h4>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={stats.campaign_leads} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis type="number" tick={TICK_MD} allowDecimals={false} />
                  <YAxis type="category" dataKey="campaign" width={120} tick={TICK_SM} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Leads by Form */}
          {stats.form_leads?.length > 0 && (
            <div className="bg-white rounded-xl border p-5">
              <h4 className="font-semibold text-slate-900 mb-4">Leads by Form</h4>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={stats.form_leads} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis type="number" tick={TICK_MD} allowDecimals={false} />
                  <YAxis type="category" dataKey="form" width={120} tick={TICK_SM} />
                  <Tooltip />
                  <Bar dataKey="count" fill="#f59e0b" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}

      {/* Empty state when no campaigns/leads yet */}
      {campaigns.length === 0 && stats?.total_leads === 0 && (
        <div className="bg-white rounded-xl border p-8 text-center">
          <BarChart3 className="mx-auto text-slate-300 mb-3" size={40} />
          <h3 className="font-semibold text-slate-700 mb-1">No campaigns or leads yet</h3>
          <p className="text-sm text-slate-400 mb-4">
            Click &ldquo;Sync Now&rdquo; to pull campaigns and leads from your Meta Ads account,<br />
            or wait for leads to arrive via webhook.
          </p>
          <Button onClick={handleSync} disabled={syncing} className="bg-blue-600 hover:bg-blue-700 text-white">
            <RefreshCw className={`mr-1.5 ${syncing ? 'animate-spin' : ''}`} size={14} /> Sync Now
          </Button>
        </div>
      )}
    </div>
  );
}
